import { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const DAYS = ['Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă', 'Duminică'];

export default function Appointments() {
  const [doctors, setDoctors] = useState([]);
  const [myDoctors, setMyDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [view, setView] = useState('list');
  const [loading, setLoading] = useState(false);
  const [videoRoom, setVideoRoom] = useState(null);
  const [success, setSuccess] = useState('');
  const [upcomingSession, setUpcomingSession] = useState(null);
  const checkInterval = useRef(null);

  useEffect(() => {
    loadDoctors();
    loadAppointments();
    loadMyDoctors();
  }, []);

  const loadMyDoctors = async () => {
    try {
      const { data } = await axios.get('/api/my-doctor');
      setMyDoctors(data);
    } catch {}
  };

  const chooseDoctor = async (doctorId) => {
    try {
      await axios.post('/api/my-doctor', { doctor_id: doctorId });
      loadMyDoctors();
    } catch(e) {
      if (e.response?.data?.error === 'Already assigned') {
        loadMyDoctors();
      }
    }
  };

  const removeMyDoctor = async (doctorId) => {
    try {
      await axios.delete(`/api/my-doctor/${doctorId}`);
      loadMyDoctors();
    } catch {}
  };

  // Verifica la fiecare 30 secunde daca o sesiune incepe in curand
  useEffect(() => {
    checkInterval.current = setInterval(() => {
      checkUpcomingSessions();
    }, 30000);
    return () => clearInterval(checkInterval.current);
  }, [appointments]);

  const checkUpcomingSessions = () => {
    const now = new Date();
    const upcoming = appointments.find(apt => {
      if (apt.status !== 'confirmed' && apt.status !== 'pending') return false;
      const aptDate = new Date(`${apt.date.slice(0,10)}T${apt.start_time}`);
      const diffMinutes = (aptDate - now) / 60000;
      return diffMinutes >= 0 && diffMinutes <= 5;
    });
    if (upcoming && (!upcomingSession || upcomingSession.id !== upcoming.id)) {
      setUpcomingSession(upcoming);
    }
  };

  useEffect(() => {
    loadDoctors();
    loadAppointments();
  }, []);

  const loadDoctors = async () => {
    try {
      const { data } = await axios.get('/api/appointments/doctors');
      setDoctors(data);
    } catch {}
  };

  const loadAppointments = async () => {
    try {
      const { data } = await axios.get('/api/appointments');
      setAppointments(data);
    } catch {}
  };

  const loadSlots = async (doctorId, date) => {
    if (!doctorId || !date) return;
    setLoading(true);
    setSlots([]);
    setSelectedSlot(null);
    try {
      const { data } = await axios.get(`/api/appointments/doctors/${doctorId}/slots?date=${date}`);
      setSlots(data.slots || []);
    } catch {}
    setLoading(false);
  };

  const parseAvailability = (avail) => {
    if (!avail) return [];
    return avail.split(',').map(s => {
      const [dayPart, timePart] = s.split(':');
      const [start, end] = timePart.split('-');
      return { day: parseInt(dayPart), start, end };
    });
  };

  const getAvailableDays = (doctor) => {
    const avail = parseAvailability(doctor.availability);
    return [...new Set(avail.map(a => a.day))];
  };

  const isDateAvailable = (doctor, dateStr) => {
    const d = new Date(dateStr);
    const dow = (d.getDay() + 6) % 7;
    return getAvailableDays(doctor).includes(dow);
  };

  const book = async () => {
    if (!selectedSlot) return;
    setLoading(true);
    try {
      const { data } = await axios.post('/api/appointments', {
        doctor_id: selectedDoctor.id,
        date: selectedDate,
        start_time: selectedSlot.start,
        end_time: selectedSlot.end,
        notes,
      });
      setSuccess('Programarea a fost înregistrată! Vei primi un email de confirmare.');
      setView('list');
      loadAppointments();
      setSelectedDoctor(null);
      setSelectedDate('');
      setSlots([]);
      setSelectedSlot(null);
      setNotes('');
    } catch (err) {
      alert(err.response?.data?.error || 'Eroare la programare');
    }
    setLoading(false);
  };

  const cancel = async (id) => {
    if (!window.confirm('Anulezi această programare?')) return;
    try {
      await axios.patch(`/api/appointments/${id}/cancel`);
      loadAppointments();
    } catch {}
  };

  const fmtDate = (d) => new Date(d).toLocaleDateString('ro-RO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const statusLabel = (s) => ({
    pending: { text: 'În așteptare', color: '#854F0B', bg: '#FAEEDA' },
    confirmed: { text: 'Confirmată', color: '#3B6D11', bg: '#EAF3DE' },
    cancelled: { text: 'Anulată', color: '#7B2D36', bg: '#FEF0F1' },
    completed: { text: 'Finalizată', color: '#7A9BA3', bg: '#F0F7F8' },
  }[s] || { text: s, color: '#7A9BA3', bg: '#F0F7F8' });

  const minDate = new Date().toISOString().split('T')[0];
  const maxDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 700 }}>Programări</div>
        <button className="btn btn-primary" style={{ padding: '8px 16px', fontSize: 13 }} onClick={() => setView(view === 'book' ? 'list' : 'book')}>
          {view === 'book' ? '← Înapoi' : '+ Programare nouă'}
        </button>
      </div>

      {/* MEDICUL MEU */}
      {view === 'list' && (
        <div style={{ marginBottom: 20 }}>
          {myDoctors.length > 0 ? (
            <div style={{ background: 'linear-gradient(135deg, #E0F5F7, #E8F9F4)', borderRadius: 12, padding: '14px 16px', marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--teal)', letterSpacing: '0.06em', marginBottom: 8 }}>MY DOCTOR</div>
              {myDoctors.map(d => (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ fontSize: 28 }}>{d.avatar || '👨‍⚕️'}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--dark)' }}>{d.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>{d.specialty}</div>
                  </div>
                  <button onClick={() => removeMyDoctor(d.id)} style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 18, cursor: 'pointer' }}>✕</button>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ background: 'var(--light)', borderRadius: 12, padding: '14px 16px', marginBottom: 12 }}>
              <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 10 }}>You don't have an assigned doctor yet.</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.06em', marginBottom: 8 }}>CHOOSE A DOCTOR</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {doctors.map(d => (
                  <div key={d.id} onClick={() => chooseDoctor(d.id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'white', borderRadius: 10, border: '1px solid var(--border)', cursor: 'pointer' }}>
                    <div style={{ fontSize: 22 }}>{d.avatar || '👨‍⚕️'}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{d.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)' }}>{d.specialty} · {d.session_duration} min</div>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--teal)', fontWeight: 600 }}>Select →</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* BANNER SESIUNE IMINENTĂ */}
      {upcomingSession && !videoRoom && (
        <div style={{
          background: 'linear-gradient(135deg, #028090, #02C39A)',
          borderRadius: 12,
          padding: '14px 16px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          animation: 'pulse 2s infinite',
        }}>
          <div style={{ fontSize: 24 }}>📹</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: 'white', fontSize: 14 }}>
              Sesiunea ta începe în curând!
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', marginTop: 2 }}>
              {upcomingSession.doctor_name} · {upcomingSession.start_time?.slice(0,5)}
            </div>
          </div>
          <button
            onClick={() => { setVideoRoom(upcomingSession.jitsi_room); setUpcomingSession(null); }}
            style={{
              background: 'white',
              color: '#028090',
              border: 'none',
              borderRadius: 8,
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            Intră acum
          </button>
          <button onClick={() => setUpcomingSession(null)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(2,128,144,0.4); }
          50% { box-shadow: 0 0 0 8px rgba(2,128,144,0); }
        }
      `}</style>

      {success && (
        <div style={{ background: '#E0F5F7', border: '1px solid #9FE1CB', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#085041' }}>
          {success}
          <button onClick={() => setSuccess('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#085041' }}>✕</button>
        </div>
      )}

      {/* VIDEO CALL */}
      {videoRoom && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Sesiune video activă</div>
            <button onClick={() => setVideoRoom(null)} style={{ fontSize: 12, color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer' }}>Închide</button>
          </div>
          <iframe
            src={`https://meet.jit.si/${videoRoom}#config.startWithVideoMuted=false&config.startWithAudioMuted=false&interfaceConfig.SHOW_JITSI_WATERMARK=false`}
            style={{ width: '100%', height: 420, border: 'none', borderRadius: 12 }}
            allow="camera; microphone; fullscreen; display-capture"
            title="Video call"
          />
        </div>
      )}

      {/* BOOK VIEW */}
      {view === 'book' && (
        <div>
          <div className="section-label">ALEGE MEDICUL</div>
          {doctors.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 13 }}>Niciun medic disponibil momentan.</div>}
          {doctors.map(doc => (
            <div key={doc.id} className="card" style={{ marginBottom: 10, cursor: 'pointer', border: selectedDoctor?.id === doc.id ? '2px solid var(--teal)' : undefined }}
              onClick={() => { setSelectedDoctor(doc); setSelectedDate(''); setSlots([]); setSelectedSlot(null); }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ fontSize: 28 }}>{doc.avatar}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{doc.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>{doc.specialty}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                    Sesiune: {doc.session_duration} min · Disponibil: {getAvailableDays(doc).map(d => DAYS[d]).join(', ')}
                  </div>
                </div>
                {selectedDoctor?.id === doc.id && <div style={{ color: 'var(--teal)', fontSize: 18 }}>✓</div>}
              </div>
              {doc.bio && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8, lineHeight: 1.4 }}>{doc.bio}</div>}
            </div>
          ))}

          {selectedDoctor && (
            <>
              <div className="section-label" style={{ marginTop: 20 }}>ALEGE DATA</div>
              <input type="date" className="input" min={minDate} max={maxDate} value={selectedDate}
                onChange={e => { setSelectedDate(e.target.value); loadSlots(selectedDoctor.id, e.target.value); }}
                style={{ marginBottom: 4 }} />
              {selectedDate && !isDateAvailable(selectedDoctor, selectedDate) && (
                <div style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 8 }}>
                  Medicul nu este disponibil în această zi. Zile disponibile: {getAvailableDays(selectedDoctor).map(d => DAYS[d]).join(', ')}
                </div>
              )}
            </>
          )}

          {selectedDate && isDateAvailable(selectedDoctor, selectedDate) && (
            <>
              <div className="section-label" style={{ marginTop: 16 }}>SLOTURI DISPONIBILE</div>
              {loading && <div style={{ color: 'var(--muted)', fontSize: 13 }}>Se încarcă...</div>}
              {!loading && slots.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 13 }}>Nu există sloturi disponibile în această zi.</div>}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                {slots.map((slot, i) => (
                  <button key={i} onClick={() => setSelectedSlot(slot)} style={{
                    padding: '8px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
                    border: `1.5px solid ${selectedSlot === slot ? 'var(--teal)' : 'var(--border)'}`,
                    background: selectedSlot === slot ? '#E0F5F7' : 'none',
                    color: selectedSlot === slot ? 'var(--teal)' : 'var(--dark)',
                    fontWeight: selectedSlot === slot ? 600 : 400,
                  }}>
                    {slot.start} — {slot.end}
                  </button>
                ))}
              </div>
            </>
          )}

          {selectedSlot && (
            <>
              <div className="form-group">
                <label className="input-label">NOTE (opțional)</label>
                <textarea className="input" style={{ minHeight: 70, resize: 'vertical' }}
                  placeholder="Descrie pe scurt motivul programării..."
                  value={notes} onChange={e => setNotes(e.target.value)} />
              </div>
              <div className="card" style={{ marginBottom: 16, background: '#E0F5F7', border: 'none' }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Rezumat programare</div>
                <div style={{ fontSize: 13, color: '#085041' }}>
                  <div>👨‍⚕️ {selectedDoctor.name} — {selectedDoctor.specialty}</div>
                  <div>📅 {fmtDate(selectedDate)}</div>
                  <div>🕐 {selectedSlot.start} — {selectedSlot.end}</div>
                </div>
              </div>
              <button className="btn btn-primary btn-full" onClick={book} disabled={loading}>
                {loading ? 'Se trimite...' : 'Confirmă programarea'}
              </button>
            </>
          )}
        </div>
      )}

      {/* LIST VIEW */}
      {view === 'list' && (
        <>
          {appointments.length === 0 && (
            <div style={{ textAlign: 'center', color: 'var(--muted)', marginTop: 40 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📅</div>
              <div>Nu ai nicio programare. Programează o sesiune cu un specialist.</div>
            </div>
          )}
          {appointments.map(apt => {
            const st = statusLabel(apt.status);
            return (
              <div key={apt.id} className="card" style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 20 }}>{apt.avatar}</span>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{apt.doctor_name}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>{apt.specialty}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--dark)', marginTop: 4 }}>
                      📅 {fmtDate(apt.date)} · 🕐 {apt.start_time.slice(0,5)} — {apt.end_time.slice(0,5)}
                    </div>
                    {apt.notes && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{apt.notes}</div>}
                    <div style={{ marginTop: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 12, background: st.bg, color: st.color }}>
                        {st.text}
                      </span>
                    </div>
                  </div>
                </div>
                {(apt.status === 'confirmed' || apt.status === 'pending') && apt.jitsi_room && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button className="btn btn-primary" style={{ fontSize: 12, padding: '7px 14px', borderRadius: 8 }}
                      onClick={() => setVideoRoom(apt.jitsi_room)}>
                      📹 Intră în sesiune
                    </button>
                    {apt.status === 'pending' && (
                      <button onClick={() => cancel(apt.id)} style={{ fontSize: 12, padding: '7px 14px', borderRadius: 8, background: 'none', border: '1px solid var(--border)', cursor: 'pointer', color: 'var(--danger)' }}>
                        Anulează
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
